import { useCallback, useEffect, useRef, useState } from "react";
import type { KosztyOkresuDto, OkresDni, ZrodloKosztow } from "@shared/kosztyTypy.js";
import { api, ApiError } from "../lib/api.js";

/**
 * Zestawienie kosztow za okres (GET /api/koszty) z odswiezaniem przy zmianie okresu i zrodla.
 *
 * Licznik zadan jak w useSesja: odpowiedz starsza niz ostatnio zastosowana jest pomijana, bo przy szybkim
 * przelaczaniu 7/30/90 odpowiedzi moga wrocic w zlej kolejnosci. Hook zwraca ostatnia udana odpowiedz tez wtedy, gdy
 * pobieramy nowa albo pobranie padlo; to STRONA decyduje, czy ta odpowiedz pasuje do wybranego okresu i zrodla
 * (KosztyPage), bo suma z innego zakresu nie moze wisiec pod nowa etykieta.
 */

export interface StanKosztow {
  dane: KosztyOkresuDto | null;
  /** True przy pierwszym pobraniu i przy kazdej zmianie parametrow. */
  ladowanie: boolean;
  /** Kod bledu z serwera albo "siec", gdy nie bylo odpowiedzi. Null, gdy ostatnie pobranie sie udalo. */
  blad: string | null;
  odswiez: () => void;
}

export function useKoszty(dni: OkresDni, zrodlo: ZrodloKosztow): StanKosztow {
  const [dane, setDane] = useState<KosztyOkresuDto | null>(null);
  const [ladowanie, setLadowanie] = useState(true);
  const [blad, setBlad] = useState<string | null>(null);
  const [wersja, setWersja] = useState(0);
  const numerZapytania = useRef(0);
  const zastosowany = useRef(0);

  useEffect(() => {
    const numer = ++numerZapytania.current;
    setLadowanie(true);
    api
      .koszty(dni, zrodlo)
      .then((odpowiedz) => {
        if (numer < zastosowany.current) return;
        zastosowany.current = numer;
        setDane(odpowiedz);
        setBlad(null);
      })
      .catch((err: unknown) => {
        if (numer < zastosowany.current) return;
        zastosowany.current = numer;
        setBlad(err instanceof ApiError ? err.code : "siec");
      })
      .finally(() => {
        if (numer === numerZapytania.current) setLadowanie(false);
      });
  }, [dni, zrodlo, wersja]);

  const odswiez = useCallback(() => setWersja((w) => w + 1), []);
  return { dane, ladowanie, blad, odswiez };
}
